// ── ReverbStatusContext ─────────────────────────────────────
// Exposes the Echo connection status (connecting | connected | polling) to
// any descendant of <ReverbStatusProvider>. Mounted inside AppLayout so the
// hook lifecycle runs exactly once per authenticated session — components
// (e.g., AppSidebar) consume `useReverbStatus()` to render the sage/stone dot.
//
// Why a Provider wrapping a hook instead of calling the hook directly in
// AppSidebar? Multiple consumers would otherwise each instantiate their own
// useReverbChannel(), which would create multiple Echo connections and
// duplicate polling intervals. The Provider owns the single instance.

import { createContext, useContext, type ReactNode } from 'react';
import { useReverbChannel, type ReverbStatus } from '@/hooks/useReverbChannel';

interface ReverbStatusValue {
  status: ReverbStatus;
}

const ReverbStatusContext = createContext<ReverbStatusValue | null>(null);

export function useReverbStatus(): ReverbStatusValue {
  const ctx = useContext(ReverbStatusContext);
  if (!ctx) {
    throw new Error('useReverbStatus must be used within ReverbStatusProvider');
  }
  return ctx;
}

export function ReverbStatusProvider({ children }: { children: ReactNode }) {
  const { status } = useReverbChannel();
  return (
    <ReverbStatusContext.Provider value={{ status }}>
      {children}
    </ReverbStatusContext.Provider>
  );
}
